/**
 * The reports' and the commissions' routes (piece 26). 🔨 Relative to the API's
 * own root, never prefixed with /api: the base URL carries the prefix, and a
 * path that repeats it 404s silently.
 */
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@bms2/shared';
export const REPORTS_PATH = '/reports';
export const PROJECT_PERFORMANCE_PATH = '/reports/project-performance';
export const SALES_DASHBOARD_PATH = '/reports/sales-dashboard';
/** T2362: the sales metrics the dashboard's Win and Loss, Hunting and Farming, and Growth and Acquisition panels draw. */
export const SALES_METRICS_PATH = '/leads/metrics';
export const LOW_STOCK_RUN_PATH = '/reports/low-stock/run';
/** 🔒 Raise Purchase Orders over the ticked Low Stock shelves (piece 22; decision 11040): the Purchase Order's own route. */
export const REPLENISHMENT_PATH = '/purchase-orders/replenishment';
export const COMMISSION_REPORT_PATH = '/commissions/report';
export const COMMISSION_REPORT_CSV_PATH = '/commissions/report.csv';
export const COMMISSION_REPORT_PDF_PATH = '/commissions/report.pdf';
/** Decision 0981: the rep's pay summary, per imported pay period, naming each recovery with its credit note. */
export const COMMISSION_PAY_SUMMARY_PATH = '/commissions/pay-summary';
export const COMMISSION_RATES_PATH = '/commissions/rates';
export const COMMISSION_TIERS_PATH = '/commissions/tiers';
export const COMMISSION_OVERRIDES_PATH = '/commissions/overrides';
export const EARNING_CODES_PATH = '/commissions/earning-codes';

/** The screens' own routes, the ones the seeded `NavItem` rows name (decision 0509). */
export const REPORTS_ROUTE = '/reports';
export const PROJECT_PERFORMANCE_ROUTE = '/reports/project-performance';
export const SALES_DASHBOARD_ROUTE = '/reports/sales-dashboard';

/** The Sales Commission Report's own route, which the Sales Reporting Dashboard's rep ranking links to with an employmentId. */
export const SALES_COMMISSION_ROUTE = '/reports/sales-commission';

/** One report's own path, with the filters the page is showing; the export routes take the same query. */
export function reportPath(key: string, query: URLSearchParams, suffix: '' | '/csv' | '/pdf' = ''): string {
  const search = query.toString();
  return `${REPORTS_PATH}/${key}${suffix}${search ? `?${search}` : ''}`;
}

/** A statement's saved views (decision 0850): the list and a save under one statement's own path. */
export function reportViewsPath(key: string): string {
  return `${REPORTS_PATH}/${key}/views`;
}

/** One saved view, which its owner renames, shares, saves over or deletes. */
export function reportViewPath(key: string, viewId: string): string {
  return `${reportViewsPath(key)}/${viewId}`;
}

/**
 * 🔒 A report download goes through `apiFetch` and is saved from a blob, never
 * a bare `<a href download>` (found in review, 2026-09-18). A link the browser
 * follows on its own carries neither the View-as headers nor the development
 * bearer, so the export the person got back was somebody else's or a refusal the
 * shell swallowed.
 *
 * 🔨 It answers whether the file was saved, so a caller that names a refusal can (T541).
 *
 * 🔨 The file's own name comes from the response, because the server already
 * names it; the fallback is used only where a test double sends no headers.
 */
export async function downloadThroughFrame(fetcher: (url: string) => Promise<Response>, url: string, fallbackName: string): Promise<boolean> {
  const response = await fetcher(url);
  if (!response.ok) return false;
  // A test double answers without headers or a body; the browser's own Response always has both.
  if (typeof response.blob !== 'function' || typeof URL.createObjectURL !== 'function') return true;
  const disposition = response.headers?.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const objectUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
  return true;
}

/**
 * 🔒 How long a PDF opened in a tab keeps its blob URL (T318): long enough for the
 * tab to have read it, then it is revoked so the page does not hold the bytes for
 * as long as it stays open. The browser's own viewer keeps what it read, so its
 * save still works; a reload of that tab after this has nothing to read.
 */
export const BLOB_TAB_REVOKE_MS = 60_000;

/** Integer cents as a person reads them, the same formatter the ledger's screens use. */
export function money(cents: number, currencyCode: string = DEFAULT_CURRENCY_CODE): string {
  return formatCurrency({ amount: cents, currencyCode });
}

/** The commission report's own name for the same formatter; the report prints money and never a bare number. */
export const reportMoney = money;
